<template>
    <div class="ai-loading d-flex flex-column align-center text-center px-6 py-12" aria-live="polite">
        <div class="ai-orb mb-9" aria-hidden="true">
            <div class="ai-orb-core"></div>
            <div class="ai-orb-ring ring-1"></div>
            <div class="ai-orb-ring ring-2"></div>
            <div class="ai-orb-ring ring-3"></div>
            <v-icon class="ai-orb-icon" color="white" size="36">mdi-auto-fix</v-icon>
            <span class="ai-spark spark-1">✦</span>
            <span class="ai-spark spark-2">✧</span>
            <span class="ai-spark spark-3">✦</span>
            <span class="ai-spark spark-4">✧</span>
        </div>

        <h2 class="ai-title stardust--text text-h5 font-weight-semibold mb-2">{{ title }}</h2>
        <p class="ai-subtitle mercury--text text-body-2 mb-6">{{ subtitle }}</p>

        <div class="ai-status mb-5" aria-live="polite">
            <transition name="status-fade" mode="out-in">
                <span :key="stepIndex" class="ai-status-text primary--text">
                    {{ steps[stepIndex] }}
                </span>
            </transition>
        </div>

        <div class="ai-progress" aria-hidden="true">
            <div class="ai-progress-bar"></div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ArtigosAiLoading',
    props: {
        title: {
            type: String,
            default: 'Nossa IA está buscando temas relevantes para você',
        },
        subtitle: {
            type: String,
            default: 'Analisando seu perfil profissional para gerar os temas mais relevantes. Isso pode levar até 3 minutos.',
        },
        steps: {
            type: Array,
            default: () => [
                'Lendo seu perfil profissional…',
                'Identificando sua especialidade e público-alvo…',
                'Mapeando dúvidas comuns dos seus pacientes…',
                'Buscando ângulos relevantes para o seu nicho…',
                'Refinando títulos com apelo editorial…',
                'Finalizando as melhores sugestões…',
            ],
        },
        interval: {
            type: Number,
            default: 2600,
        },
    },
    data() {
        return {
            stepIndex: 0,
            timer: null,
        }
    },
    mounted() {
        this.timer = setInterval(() => {
            this.stepIndex = (this.stepIndex + 1) % this.steps.length
        }, this.interval)
    },
    beforeDestroy() {
        if (this.timer) clearInterval(this.timer)
    },
}
</script>

<style scoped>
.ai-loading {
    min-height: calc(100vh - 120px);
    position: relative;
    overflow: hidden;
}

.ai-loading::before {
    content: '';
    position: absolute;
    top: 10%;
    left: 50%;
    width: 720px;
    height: 720px;
    transform: translateX(-50%);
    background:
        radial-gradient(circle at center, rgba(0, 115, 230, 0.10) 0%, transparent 55%),
        radial-gradient(circle at 30% 70%, rgba(112, 105, 161, 0.09) 0%, transparent 55%);
    filter: blur(20px);
    pointer-events: none;
    animation: ambient-drift 12s ease-in-out infinite alternate;
}

@keyframes ambient-drift {
    0%   { transform: translateX(-50%) scale(1); }
    100% { transform: translateX(-52%) scale(1.08); }
}

/* Orb */
.ai-orb {
    position: relative;
    width: 160px;
    height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ai-orb-core {
    position: absolute;
    inset: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-galaxy-base) 100%);
    box-shadow:
        0 0 40px rgba(0, 115, 230, 0.55),
        0 0 80px rgba(112, 105, 161, 0.35),
        inset 0 -10px 20px rgba(15, 21, 28, 0.15);
    animation: orb-pulse 2.4s ease-in-out infinite;
}

@keyframes orb-pulse {
    0%, 100% { transform: scale(1);    box-shadow: 0 0 40px rgba(0, 115, 230, 0.55), 0 0 80px rgba(112, 105, 161, 0.35), inset 0 -10px 20px rgba(15, 21, 28, 0.15); }
    50%      { transform: scale(1.06); box-shadow: 0 0 56px rgba(0, 115, 230, 0.75), 0 0 110px rgba(112, 105, 161, 0.45), inset 0 -10px 20px rgba(15, 21, 28, 0.15); }
}

.ai-orb-icon {
    position: relative;
    z-index: 2;
    animation: icon-spin 6s linear infinite;
}

@keyframes icon-spin {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.ai-orb-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(0, 115, 230, 0.35);
    animation: ring-expand 2.6s ease-out infinite;
    opacity: 0;
}

.ai-orb-ring.ring-1 { inset: 20px; animation-delay: 0s; }
.ai-orb-ring.ring-2 { inset: 20px; animation-delay: 0.9s; }
.ai-orb-ring.ring-3 { inset: 20px; animation-delay: 1.8s; border-color: rgba(112, 105, 161, 0.35); }

@keyframes ring-expand {
    0%   { transform: scale(0.85); opacity: 0.7; }
    80%  { opacity: 0; }
    100% { transform: scale(1.6);  opacity: 0; }
}

/* Sparkles */
.ai-spark {
    position: absolute;
    font-size: 16px;
    animation: spark-twinkle 2.2s ease-in-out infinite;
    text-shadow: 0 0 10px rgba(0, 115, 230, 0.6);
}

.ai-spark.spark-1 { top: 4%;  left: 12%;  animation-delay: 0s;   color: var(--v-primary-base); }
.ai-spark.spark-2 { top: 8%;  right: 6%;  animation-delay: 0.6s; color: var(--v-galaxy-base); font-size: 12px; }
.ai-spark.spark-3 { bottom: 10%; left: 4%;  animation-delay: 1.1s; color: var(--v-galaxy-base); font-size: 14px; }
.ai-spark.spark-4 { bottom: 2%;  right: 14%; animation-delay: 1.6s; color: var(--v-primary-base); font-size: 18px; }

@keyframes spark-twinkle {
    0%, 100% { opacity: 0;   transform: scale(0.6) rotate(0deg); }
    50%      { opacity: 1;   transform: scale(1.1) rotate(20deg); }
}

/* Text */
.ai-title {
    letter-spacing: -0.01em;
    max-width: 560px;
}

.ai-subtitle {
    max-width: 520px;
    line-height: 1.55;
}

.ai-status {
    min-height: 22px;
}

.ai-status-text {
    display: inline-block;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.01em;
}

.status-fade-enter-active,
.status-fade-leave-active {
    transition: opacity 400ms ease, transform 400ms ease;
}

.status-fade-enter {
    opacity: 0;
    transform: translateY(4px);
}

.status-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

/* Progress bar (indeterminate flow) */
.ai-progress {
    width: 240px;
    height: 3px;
    background: rgba(112, 105, 161, 0.15);
    border-radius: 999px;
    overflow: hidden;
    position: relative;
}

.ai-progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 45%;
    background: linear-gradient(90deg, transparent, var(--v-primary-base) 40%, var(--v-galaxy-base) 80%, transparent);
    border-radius: 999px;
    animation: progress-flow 1.6s cubic-bezier(0.6, 0, 0.4, 1) infinite;
}

@keyframes progress-flow {
    0%   { transform: translateX(-120%); }
    100% { transform: translateX(240%); }
}

@media (prefers-reduced-motion: reduce) {
    .ai-orb-core,
    .ai-orb-icon,
    .ai-orb-ring,
    .ai-spark,
    .ai-progress-bar,
    .ai-loading::before {
        animation: none !important;
    }
}
</style>
